import { useState, useEffect } from 'react';
import './StatusTiles.css';

import { FlaskConical } from "lucide-react";


export default function StatusTiles({ telemetry, onMove, simulationEnabled, onToggleSimulation, isRealData }) {
  // -------------------------------
  // CONFIGURATION
  // -------------------------------
  const moveAmount = 0.001; // ~5 meters depending on latitude
  const defaultLat = 49.2615;
  const defaultLon = -123.2490;

  // -------------------------------
  // LOCAL STATE
  // -------------------------------
  const [manualLat, setManualLat] = useState(telemetry?.lat ?? defaultLat);
  const [manualLon, setManualLon] = useState(telemetry?.lon ?? defaultLon);

  const formatNumber = (value, digits) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) return value;
    return value.toFixed(digits);
  };

  // -------------------------------
  // EFFECTS
  // -------------------------------
  // Sync manual inputs with telemetry when telemetry changes
  useEffect(() => {
    if (telemetry?.lat != null) setManualLat(telemetry.lat);
    if (telemetry?.lon != null) setManualLon(telemetry.lon);
  }, [telemetry?.lat, telemetry?.lon]);

  // -------------------------------
  // HANDLERS
  // -------------------------------
  const handleSetPosition = () => {
    const dLat = parseFloat(manualLat) - (telemetry?.lat ?? defaultLat);
    const dLon = parseFloat(manualLon) - (telemetry?.lon ?? defaultLon);
    onMove(dLat, dLon);
  };

  // -------------------------------
  // LIVE TELEMETRY
  // -------------------------------
  const renderTelemetry = () => {
    const showData = simulationEnabled || isRealData;
    return (
      <>
        <div className="telemetry-row">Battery: {showData ? (formatNumber(telemetry?.bat_pct, 0) ?? '--') : '--'}%</div>
        <div className="telemetry-row">Link: {showData ? (formatNumber(telemetry?.link, 0) ?? '--') : '--'}%</div>
        <div className="telemetry-row">Mode: {showData ? (telemetry?.mode ?? '--') : '--'}</div>
        <div className="telemetry-row">Failsafe: {showData ? (telemetry?.failsafe ?? '--') : '--'}</div>
        <div className="telemetry-row">Altitude: {showData ? (formatNumber(telemetry?.alt, 2) ?? '--') : '--'} m</div>
        <div className="telemetry-row">Latitude: {showData ? formatNumber(telemetry?.lat ?? defaultLat, 5) : '--'}</div>
        <div className="telemetry-row">Longitude: {showData ? formatNumber(telemetry?.lon ?? defaultLon, 5) : '--'}</div>
        <div className="telemetry-row">Heading/Yaw: {showData ? (formatNumber(telemetry?.yaw, 1) ?? '--') : '--'}°</div>
        <div className="telemetry-row">Wind estimate: {showData ? (formatNumber(telemetry?.wind_est, 2) ?? '--') : '--'} m/s</div>
        <div className="telemetry-row">Payload: {showData ? (telemetry?.payload_state ?? '--') : '--'}</div>
        <div className="telemetry-row">Temperature: {showData ? (formatNumber(telemetry?.temperature_c, 1) ?? '--') : '--'}°C</div>
        <div className="telemetry-row">Pressure: {showData ? (formatNumber(telemetry?.pressure_hpa, 1) ?? '--') : '--'} hPa</div>
        <div className="telemetry-row">IMU Pitch: {showData ? (formatNumber(telemetry?.imu_pitch_deg, 1) ?? '--') : '--'}°</div>
        <div className="telemetry-row">IMU Roll: {showData ? (formatNumber(telemetry?.imu_roll_deg, 1) ?? '--') : '--'}°</div>
        <div className="telemetry-row">Battery Voltage: {showData ? (formatNumber(telemetry?.battery_voltage_mv, 0) ?? '--') : '--'} mV</div>
        <div className="telemetry-row">Current Draw: {showData ? (formatNumber(telemetry?.current_draw_ma, 0) ?? '--') : '--'} mA</div>
        <div className="telemetry-row">Power Draw: {showData ? (formatNumber(telemetry?.power_draw_mw, 0) ?? '--') : '--'} mW</div>
        <div className="telemetry-row">Camera Position: {showData ? (formatNumber(telemetry?.camera_pos_deg, 1) ?? '--') : '--'}°</div>
        <div className="telemetry-row">Target Yaw: {showData ? (formatNumber(telemetry?.target_yaw_deg, 1) ?? '--') : '--'}°</div>
      </>
    );
  };

  // -------------------------------
  // RENDER SIMULATION MOVEMENT CONTROLS
  // -------------------------------
  const renderSimulationControls = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
      <button onClick={() => onMove(moveAmount, 0)}>⬆</button>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={() => onMove(0, -moveAmount)}>⬅</button>
        <button onClick={() => onMove(0, moveAmount)}>➡</button>
      </div>
      <button onClick={() => onMove(-moveAmount, 0)}>⬇</button>
    </div>
  );

  // -------------------------------
  // RENDER MANUAL COORDINATE INPUT
  // -------------------------------
  const renderManualCoordInput = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }}>
      <input
        type="number"
        step="0.00001"
        placeholder="Latitude"
        value={manualLat}
        onChange={(e) => setManualLat(e.target.value)}
        style={{ width: '80px', textAlign: 'center' }}
      />
      <input
        type="number"
        step="0.00001"
        placeholder="Longitude"
        value={manualLon}
        onChange={(e) => setManualLon(e.target.value)}
        style={{ width: '80px', textAlign: 'center' }}
      />
      <button onClick={handleSetPosition}>Set Position</button>
    </div>
  );

  // -------------------------------
  // MAIN RENDER
  // -------------------------------

  return (
    <div className="status-tiles">

      {/* TELEMETRY DISPLAY */}
      {renderTelemetry()}

      {/* SIMULATION TOGGLE BUTTON */}
      <button
        className="control-btn special save-view-btn"
        onClick={onToggleSimulation}
      >
        {simulationEnabled ? "Simulation On" : "Simulation Off"}
      </button>

      {/* SIMULATION SECTIONS (shown when simulation is enabled) */}
      {simulationEnabled && (
        <>
          <h4 style={{ marginTop: '12px' }}>SIMULATION: DRONE MOVEMENT</h4>
          {renderSimulationControls()}

          <h4 style={{ marginTop: '12px' }}>SIMULATION: SET DRONE COORDS</h4>
          {renderManualCoordInput()}
        </>
      )}

    </div>
  );

}
